import { observer } from 'mobx-react-lite'
import React, { useContext } from 'react'
import { I18nextProvider } from 'react-i18next'
import { Route, Routes, useResolvedPath } from 'react-router-dom'

import {
  ErrorBoundary,
  ErrorComponent,
  InviteFailure,
  InviteStep2,
  InviteStep3,
  InviteStep4,
  ViewLoading,
} from '../components'
import { NavRoute } from '../components/NavRoute'
import { StoreContext } from '../store'
import { i18nInstance } from '../utils'
import { CoreContextBrowserRouter, useFeatureBasename } from '../utils/CoreContextBrowserRouter'
import { relativeFeaturePath } from '../utils/relativeFeaturePath'
import { EditUserView } from './editUserView'
import { InviteDetailsView } from './inviteDetailsView'
import { InviteView } from './inviteView'
import { MainView } from './mainView'
import { OrganisationsView } from './organisationsView'
import { LegacyOrganisationRedirect, SingleOrgView } from './singleOrgView'
import { UserDetailView } from './userDetailView'

export const IndexComponent: React.FC<{
  basename: string
}> = ({ basename }) => {
  return (
    <I18nextProvider i18n={i18nInstance}>
      <CoreContextBrowserRouter basename={basename}>
        <ErrorBoundary>
          <IndexContent />
        </ErrorBoundary>
      </CoreContextBrowserRouter>
    </I18nextProvider>
  )
}

const IndexContent: React.FC = observer(() => {
  const basename = useFeatureBasename()
  const parentPath = useResolvedPath('.').pathname.replace(/\/+$/, '')
  // Descendant Routes match relative to Core's matched route, including exact entry routes.
  const routes = [
    { path: '', element: <MainView /> },
    { path: 'invite/1', element: <InviteView /> },
    { path: 'invite/2', element: <InviteStep2 /> },
    { path: 'invite/3', element: <InviteStep3 /> },
    { path: 'invite/4', element: <InviteStep4 /> },
    { path: 'invite/failure', element: <InviteFailure /> },
    { path: 'organisations', element: <OrganisationsView /> },
    { path: 'organisations/:id', element: <SingleOrgView /> },
    { path: 'organisation/:id', element: <LegacyOrganisationRedirect /> },
    { path: 'user/:id', element: <UserDetailView /> },
    { path: 'invite-detail/:id', element: <InviteDetailsView /> },
    { path: 'edit-user/:userid/:orgid', element: <EditUserView /> },
  ].flatMap(({ path, element }) => {
    const relativePath = relativeFeaturePath(`${basename}/${path}`, parentPath)

    return relativePath === null ? [] : [{ path: relativePath, element }]
  })
  const { userStore, searchStore } = useContext(StoreContext)

  if (!searchStore.firstLoadDone) {
    return <ViewLoading />
  }

  const renderContent = () => {
    if (userStore.error) {
      return <ErrorComponent error="general" />
    }

    return (
      <Routes>
        <Route element={<NavRoute />}>
          {routes.map(({ path, element }) =>
            path === '' ? (
              <Route key="index" index element={element} />
            ) : (
              <Route key={path} path={path} element={element} />
            )
          )}
          <Route path="*" errorElement={<ErrorComponent error="badUrl" />} />
        </Route>
      </Routes>
    )
  }

  return renderContent()
})

export const Index = observer(IndexComponent)
